import { jsx as _jsx } from "react/jsx-runtime";
/**
 * RichTextMorph - Blur morph effect for rich text with links
 *
 * Combines the elegant blur effect from TextMorph with support for
 * Markdown links. Used for product linking agent where AI inserts
 * product links into existing content.
 *
 * Features:
 * - Character-by-character blur transition for text changes
 * - Special highlight effect for newly inserted links
 * - Smooth fade-in with blur for new link elements
 * - GPU-optimized animations using framer-motion
 *
 * @module feedback/AIGenerating
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
// ============================================
// CONSTANTS
// ============================================
const BLUR_AMOUNT = '4px';
const CHAR_DURATION_MS = 25;
// ============================================
// UTILITIES
// ============================================
/**
 * Parse text and extract Markdown links
 */
function parseTextWithLinks(text) {
    const segments = [];
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;
    let segmentIndex = 0;
    while ((match = linkRegex.exec(text)) !== null) {
        // Text before link
        if (match.index > lastIndex) {
            segments.push({
                type: 'text',
                content: text.slice(lastIndex, match.index),
                key: `text-${segmentIndex++}`,
            });
        }
        // Link
        segments.push({
            type: 'link',
            content: match[1], // anchor text
            url: match[2],
            key: `link-${segmentIndex++}`,
        });
        lastIndex = match.index + match[0].length;
    }
    // Remaining text
    if (lastIndex < text.length) {
        segments.push({
            type: 'text',
            content: text.slice(lastIndex),
            key: `text-${segmentIndex}`,
        });
    }
    return segments;
}
/**
 * Get plain text without links (for diffing)
 */
function getPlainText(text) {
    return text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}
/**
 * Simple character diff algorithm
 */
function getCharDiffs(oldText, newText) {
    if (oldText === newText) {
        return newText.split('').map((char, idx) => ({
            type: 0,
            char,
            key: `eq-${idx}`,
        }));
    }
    if (!oldText) {
        return newText.split('').map((char, idx) => ({
            type: 1,
            char,
            key: `ins-${idx}`,
        }));
    }
    if (!newText) {
        return oldText.split('').map((char, idx) => ({
            type: -1,
            char,
            key: `del-${idx}`,
        }));
    }
    // Find common prefix
    let prefixLen = 0;
    const minLen = Math.min(oldText.length, newText.length);
    while (prefixLen < minLen && oldText[prefixLen] === newText[prefixLen]) {
        prefixLen++;
    }
    // Find common suffix
    let suffixLen = 0;
    while (suffixLen < minLen - prefixLen &&
        oldText[oldText.length - 1 - suffixLen] === newText[newText.length - 1 - suffixLen]) {
        suffixLen++;
    }
    const result = [];
    // Prefix (equal)
    for (let i = 0; i < prefixLen; i++) {
        result.push({ type: 0, char: oldText[i], key: `pf-${i}` });
    }
    // Deleted middle
    const oldMiddle = oldText.slice(prefixLen, oldText.length - suffixLen);
    for (let i = 0; i < oldMiddle.length; i++) {
        result.push({ type: -1, char: oldMiddle[i], key: `del-${i}` });
    }
    // Inserted middle
    const newMiddle = newText.slice(prefixLen, newText.length - suffixLen);
    for (let i = 0; i < newMiddle.length; i++) {
        result.push({ type: 1, char: newMiddle[i], key: `ins-${i}` });
    }
    // Suffix (equal)
    const suffix = newText.slice(newText.length - suffixLen);
    for (let i = 0; i < suffix.length; i++) {
        result.push({ type: 0, char: suffix[i], key: `sf-${i}` });
    }
    return result;
}
// ============================================
// ANIMATED CHARACTER (with blur effect)
// ============================================
const AnimatedChar = React.memo(({ char, type, delay, speed = 1, }) => {
    const duration = CHAR_DURATION_MS / 1000 / speed;
    const delayMs = delay * (CHAR_DURATION_MS / 1000 / speed);
    if (type === 0) {
        return _jsx("span", { children: char });
    }
    if (type === -1) {
        // Deleted character: blur out + fade out
        return (_jsx(motion.span, { initial: {
                opacity: 1,
                width: 'auto',
                filter: 'blur(0px)',
            }, animate: {
                opacity: 0,
                width: 0,
                filter: `blur(${BLUR_AMOUNT})`,
            }, transition: {
                duration,
                delay: delayMs,
                ease: 'easeOut',
            }, style: {
                display: 'inline-block',
                overflow: 'hidden',
                whiteSpace: 'pre',
                color: '#dc2626',
                textDecoration: 'line-through',
                backgroundColor: 'rgba(254, 226, 226, 0.5)',
            }, children: char }));
    }
    // Inserted character: blur in + fade in
    return (_jsx(motion.span, { initial: {
            opacity: 0,
            width: 0,
            filter: `blur(${BLUR_AMOUNT})`,
        }, animate: {
            opacity: 1,
            width: 'auto',
            filter: 'blur(0px)',
        }, transition: {
            duration,
            delay: delayMs,
            ease: 'easeOut',
        }, style: {
            display: 'inline-block',
            overflow: 'hidden',
            whiteSpace: 'pre',
            color: '#16a34a',
            backgroundColor: 'rgba(220, 252, 231, 0.5)',
        }, children: char }));
});
AnimatedChar.displayName = 'AnimatedChar';
// ============================================
// ANIMATED LINK (special effect for new links)
// ============================================
const AnimatedLink = React.memo(({ content, url, delay, speed = 1, isNew = false, }) => {
    const duration = 0.6 / speed;
    const delayMs = delay * (CHAR_DURATION_MS / 1000 / speed);
    if (!isNew) {
        // Existing link - just render normally
        return (_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "text-primary-600 hover:text-primary-700 underline decoration-primary-300 hover:decoration-primary-500 transition-colors", children: content }));
    }
    // New link - animate in with blur + highlight
    return (_jsx(motion.a, { href: url, target: "_blank", rel: "noopener noreferrer", initial: {
            opacity: 0,
            filter: `blur(${BLUR_AMOUNT})`,
            backgroundColor: 'rgba(147, 51, 234, 0)',
            scale: 0.95,
        }, animate: {
            opacity: 1,
            filter: 'blur(0px)',
            backgroundColor: ['rgba(147, 51, 234, 0.2)', 'rgba(147, 51, 234, 0.1)', 'rgba(147, 51, 234, 0)'],
            scale: 1,
        }, transition: {
            duration,
            delay: delayMs,
            ease: 'easeOut',
            backgroundColor: {
                duration: duration * 2,
                delay: delayMs,
                times: [0, 0.5, 1],
            },
        }, className: "text-primary-600 hover:text-primary-700 underline decoration-primary-300 hover:decoration-primary-500 transition-colors rounded px-0.5 -mx-0.5", style: { display: 'inline-block' }, children: content }));
});
AnimatedLink.displayName = 'AnimatedLink';
// ============================================
// MAIN COMPONENT
// ============================================
export function RichTextMorph({ oldText, newText, speed = 1, onComplete, className = '', showLinkHighlights = true, }) {
    const [isAnimating, setIsAnimating] = useState(true);
    const hasStarted = useRef(false);
    const timeoutRef = useRef();
    // Parse both texts
    const oldPlain = useMemo(() => getPlainText(oldText), [oldText]);
    const newSegments = useMemo(() => parseTextWithLinks(newText), [newText]);
    const newPlain = useMemo(() => getPlainText(newText), [newText]);
    // Check which links are new (not in original)
    const newLinksSet = useMemo(() => {
        const oldLinks = new Set();
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let match;
        while ((match = linkRegex.exec(oldText)) !== null) {
            oldLinks.add(match[2]); // URL
        }
        return oldLinks;
    }, [oldText]);
    // Calculate diffs for text parts
    const textDiffs = useMemo(() => {
        return getCharDiffs(oldPlain, newPlain);
    }, [oldPlain, newPlain]);
    // Calculate total animation duration
    const totalDuration = useMemo(() => {
        let charCount = 0;
        textDiffs.forEach(d => {
            if (d.type !== 0)
                charCount++;
        });
        // Add time for link animations
        const linkCount = newSegments.filter(s => s.type === 'link').length;
        return (charCount * CHAR_DURATION_MS / speed) + (linkCount * 300 / speed) + 500;
    }, [textDiffs, newSegments, speed]);
    // Track animation completion
    useEffect(() => {
        if (hasStarted.current)
            return;
        hasStarted.current = true;
        timeoutRef.current = window.setTimeout(() => {
            setIsAnimating(false);
            onComplete?.();
        }, totalDuration);
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [totalDuration, onComplete]);
    // Build rendered content with proper positioning
    const buildAnimatedContent = useCallback(() => {
        const elements = [];
        let plainCharIndex = 0;
        let animationDelay = 0;
        newSegments.forEach((segment, segmentIndex) => {
            if (segment.type === 'link') {
                const isNewLink = !newLinksSet.has(segment.url);
                elements.push(_jsx(AnimatedLink, { content: segment.content, url: segment.url, delay: animationDelay, speed: speed, isNew: showLinkHighlights && isNewLink }, segment.key));
                // Skip past link text in plain char index
                plainCharIndex += segment.content.length;
                animationDelay += segment.content.length;
            }
            else {
                // Text segment - render with char diffs
                const chars = segment.content.split('');
                chars.forEach((char, charIndex) => {
                    // Find corresponding diff for this character position
                    const absoluteIndex = plainCharIndex + charIndex;
                    // Find the diff that corresponds to this position
                    let diffIndex = 0;
                    let currentPos = 0;
                    for (let i = 0; i < textDiffs.length; i++) {
                        const diff = textDiffs[i];
                        if (diff.type === -1) {
                            // Deleted chars don't count towards new text position
                            continue;
                        }
                        if (currentPos === absoluteIndex) {
                            diffIndex = i;
                            break;
                        }
                        currentPos++;
                    }
                    const diff = textDiffs[diffIndex];
                    const charType = diff?.type ?? 0;
                    elements.push(_jsx(AnimatedChar, { char: char, type: charType, delay: animationDelay, speed: speed }, `${segment.key}-char-${charIndex}`));
                    if (charType !== 0) {
                        animationDelay++;
                    }
                });
                plainCharIndex += chars.length;
            }
        });
        // Add deleted characters that aren't in new text
        textDiffs
            .filter(d => d.type === -1)
            .forEach((diff, idx) => {
            elements.unshift(_jsx(AnimatedChar, { char: diff.char, type: -1, delay: idx, speed: speed }, `deleted-${idx}`));
        });
        return elements;
    }, [newSegments, textDiffs, newLinksSet, speed, showLinkHighlights]);
    // After animation, show clean final state
    if (!isAnimating) {
        return (_jsx("span", { className: className, children: newSegments.map((segment) => {
                if (segment.type === 'link') {
                    return (_jsx("a", { href: segment.url, target: "_blank", rel: "noopener noreferrer", className: "text-primary-600 hover:text-primary-700 underline decoration-primary-300 hover:decoration-primary-500 transition-colors", children: segment.content }, segment.key));
                }
                return _jsx("span", { children: segment.content }, segment.key);
            }) }));
    }
    return (_jsx("span", { className: `inline ${className}`, children: buildAnimatedContent() }));
}
export default RichTextMorph;
